import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { filter, switchMap, catchError, skip, take, tap, map } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { PageRequestDTO, PageResponseDTO } from '../../../../common/paging-dto';
import { SystemService } from '../../../../system/system.service';
import { MaintainService } from '../maintain.service';
import {
  ActionConfigDTO,
  ConditionalRowDetailsDTO,
  FieldConfigDTO,
  MaintainTabDTO,
  MenuDTO,
  MenuType,
} from '../../../../system/system-dtos';
import { EnvironmentService } from '../../../../common/services/env-configuration/environment.service';
import { UiValidatorType } from '../../../../common/field-validation/ui-validator-type';
import { TickerDTO } from '../../../../common/event-dtos';
import { MaintainFiltersComponent } from './maintain-filters/maintain-filters.component';
import { DataSource, PAGINATION_MODE } from '../data-source/data-source';
import { MaintainTableRowActionsComponent } from './maintain-table-row-actions/maintain-table-row-actions.component';
import { SignInService } from '../../../../common/services/sign-in/sign-in.service';
import { MaintainTableTabsComponent } from './maintain-table-tabs/maintain-table-tabs.component';
import { LmTitleService } from '../../../../common/services/title.service';
import { DataSourceFactory } from '../data-source/data-source-factory';
import { EncodingUtils } from '../../../../common/encoding-utils';
import { DataType } from '../../../data-type-widgets/data-type/data-type-component';
import { isDefined, notDefined } from '../../../utils/utils';

@Component({
  selector: 'app-maintain-table',
  templateUrl: './maintain-table.component.html',
  styleUrls: ['./maintain-table.component.scss'],
  standalone: false,
})
export class MaintainTableComponent implements OnInit, OnDestroy {
  @Input() private code: string;

  @Input() public id: string;

  @Input() private groupId: string;

  @Input() public context = {};

  @Input() public nested = false;

  @Input() public entityCode?: string;

  @ViewChild(MatTable) private matTable: MatTable<TickerDTO>;

  private matSort: MatSort;

  public mode: number;

  public inMemoryFilterValue: string;

  public overallActions: ActionConfigDTO[] = [];

  @ViewChild(MatSort) set setSort(content: ElementRef<MatSort>) {
    if (content) {
      this.matSort = content as any;
    }
  }

  private _tabsComponent: MaintainTableTabsComponent;

  @ViewChild(MaintainTableTabsComponent) set tabsComponent(tabsComponent: ElementRef<MaintainTableTabsComponent>) {
    if (tabsComponent) {
      this._tabsComponent = tabsComponent as any;
    }
  }

  private maintainFilterComponent: MaintainFiltersComponent;

  @ViewChild(MaintainFiltersComponent) set maintainFilterComponentSetter(
    maintainFilterComponent: ElementRef<MaintainFiltersComponent>
  ) {
    if (maintainFilterComponent) {
      this.maintainFilterComponent = maintainFilterComponent as any;
    }
  }

  private _matPaginator: MatPaginator;

  @ViewChild(MatPaginator) set matPaginator(matPaginator: ElementRef<MatPaginator>) {
    if (matPaginator) {
      this._matPaginator = matPaginator as any;
      this.initializePaging();
    }
  }

  public dataSource: DataSource;

  public menuConfig: MenuDTO;

  public data: PageResponseDTO<any>;

  public tableData: MatTableDataSource<any>;

  public allFields: FieldConfigDTO[];

  public displayedFieldNames: string[];

  public filterableFields: FieldConfigDTO[];

  public filteredFieldNames: string[] = [];

  public loading = true;

  public tabs: MaintainTabDTO[];

  public currentTab: MaintainTabDTO;

  public filterOn = false;

  public filters: { [p: string]: FieldConfigDTO };

  public filtersNotFilled = false;

  actions: MaintainTableRowActionsComponent[] = [];

  private mainSubscription: Subscription;

  private dataSourceSubscription: Subscription;

  private sessionUserEmail = this.signInService.getCurrentUser().email;

  private codeChanged: boolean;

  private timestamp: FieldConfigDTO = {
    name: '@timestamp',
    label: 'Timestamp',
    type: DataType.date,
    displayInTable: true,
    dateFormat: 'DD-MM-YY HH:mm:ss.SSS',
  };

  actionInProgress = false;

  public static getConditionalByTab(element: any, tab: MaintainTabDTO): ConditionalRowDetailsDTO {
    if (notDefined(tab.tableView.conditionals)) {
      return undefined;
    }
    let conditionForRow = tab.tableView.conditionals.find((row: ConditionalRowDetailsDTO) => {
      if (notDefined(row.conditions)) {
        return true;
      }
      const filtered = row.conditions.filter((c) => element[c.field] === c.value);
      return filtered.length === row.conditions.length;
    });

    if (!conditionForRow) {
      conditionForRow = tab.tableView.conditionals.find((row: ConditionalRowDetailsDTO) => row.conditions.length === 0);
    }

    return conditionForRow;
  }

  constructor(
    private systemService: SystemService,
    private maintainService: MaintainService,
    private route: ActivatedRoute,
    private router: Router,
    private envService: EnvironmentService,
    private dataSourceFactory: DataSourceFactory,
    private changeDetector: ChangeDetectorRef,
    private signInService: SignInService,
    private lmTitleService: LmTitleService,
    private domSanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.tableData = new MatTableDataSource<any>();

    this.mainSubscription = this.route.queryParamMap
      .pipe(
        switchMap((qpm) => {
          this.mode = Number(qpm.get('_mode') ? qpm.get('_mode') : 0);
          return this.loadData(0);
        })
      )
      .subscribe((d) => {});
  }

  private initializeSorting() {
    if (!this.dataSource) {
      return;
    }

    this.tableData.sort = this.matSort;

    const params: Params = this.route.snapshot.queryParams;
    if (params.order) {
      const split = params.order.split(',');
      const field: FieldConfigDTO = this.allFields.find(
        (f) =>
          f.sortableField &&
          (f.sortableField === split[0] ||
            EncodingUtils.camelToKebabCase(f.sortableField) === split[0] ||
            EncodingUtils.kebabCaseToCamel(f.sortableField) === split[0])
      );
      if (isDefined(field) && isDefined(this.matSort)) {
        this.matSort.active = field.name;
        this.matSort.direction = split[1].toLowerCase();
      }
    }
  }

  private initializePaging() {
    if (!this.tableData) {
      return;
    }

    if (this.dataSource.getPageMode(this.menuConfig) === PAGINATION_MODE.IN_MEMORY) {
      this.tableData.paginator = this._matPaginator;
    }
  }

  private loadData(skipNo: number = 0) {
    return this.route.paramMap
      .pipe(skip(skipNo))
      .pipe(
        tap((pm) => {
          const newCode =
            pm.get('entityCode')?.split('?')[0] ?? this.route.snapshot.data['entityCodeOverride'] ?? this.entityCode;
          this.codeChanged = this.code != newCode;
          this.code = newCode;
          this.id = pm.get('entityId') ?? this.route.parent.snapshot.paramMap.get('entityId');
          this.groupId = pm.get('groupId');
          if (this.dataSourceSubscription) {
            this.dataSourceSubscription.unsubscribe();
          }
          this.filters = null;
          this.filtersNotFilled = false;
          this.loading = true;
          this.actionInProgress = true;
        })
      )
      .pipe(switchMap(() => this.systemService.getMenuConfig(this.code)))
      .pipe(
        tap((menuConfig: MenuDTO) => {
          this.menuConfig = this.groupId
            ? menuConfig.entityGroup.menuItems.filter((mi) => mi.code === this.groupId)[0]
            : menuConfig;
          if (this.menuConfig !== undefined) {
            this.dataSource = this.dataSourceFactory.get(this.menuConfig.menuType);
          }

          if (!this.nested) {
            this.lmTitleService.setTitleSuffix(this.getTableTitle());
          }

          if (this.menuConfig.entityGroup) {
            this.menuConfig.entityGroup.currentMode = this.mode;
          }
          // gbw
          if (this.dataSource !== undefined && this.dataSource !== null) {
            this.overallActions = this.dataSource.getOverallRestActions(this.menuConfig);
          }
        }),
        filter(() => this.ensureRouteDefaultFilters()),
        filter(() => this.initializePagingIfNecessary()),
        tap(() => this.handleMultiTabView()),
        filter(() => this.navigateToDefaultSortingAndFilters()),
        switchMap(() => this.doLoadDataAndFInishProcesisng()),
        catchError((err) => {
          return err;
        })
      );
  }

  private ensureRouteDefaultFilters(): boolean {
    const defaults = this.route.snapshot.data['defaultFilters'] as Record<string, string> | undefined;
    if (!defaults) {
      return true;
    }
    const params: Params = this.route.snapshot.queryParams;
    const missing: Params = {};
    Object.entries(defaults).forEach(([key, value]) => {
      if (params[key] !== value) {
        missing[key] = value;
      }
    });
    if (Object.keys(missing).length === 0) {
      return true;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: missing,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    return false;
  }

  private navigateToDefaultSortingAndFilters(): boolean {
    const params: Params = this.route.snapshot.queryParams;
    if (params.order) {
      return true;
    }
    if (!this.currentTab.tableView.defaultSort) {
      return true;
    }

    const defaultFilters = this.getDefaultFilters();
    const sortString = `${this.currentTab.tableView.defaultSort.field},${this.currentTab.tableView.defaultSort.direction}`;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...defaultFilters,
        order: sortString,
      },
      queryParamsHandling: 'merge',
    });
    return false;
  }

  private getDefaultFilters(): Params {
    const params: Params = this.route.snapshot.queryParams;
    const defaultParams: Params = {};

    this.getCurrentTab()
      .tableView.filters.filter((filter) => !params[filter.name])
      .filter((filter) => filter.sessionUserEmailFilter)
      .filter(() => !this.codeChanged)
      .forEach((filter) => {
        defaultParams[filter.name] = this.sessionUserEmail;
      });
    return defaultParams;
  }

  private initializePagingIfNecessary(): boolean {
    const pageMode: PAGINATION_MODE = this.dataSource.getPageMode(this.menuConfig);

    if (pageMode === PAGINATION_MODE.NONE) {
      return true;
    }
    if (pageMode === PAGINATION_MODE.SERVER_SIDE || pageMode === PAGINATION_MODE.IN_MEMORY) {
      const params: Params = this.route.snapshot.queryParams;

      const { offset } = params;
      const { perPage } = params;

      if (!offset || !perPage) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            offset: 0,
            perPage: 50,
          },
          queryParamsHandling: 'merge',
        });
        return false;
      }
      return true;
    }
    return true;
  }

  doLoadDataAndFInishProcesisng() {
    this.actionInProgress = true;
    const pageRequest = this.getCurrentPage();
    return this.getData(pageRequest)
      .pipe(tap((data) => this.constructTableData(data)))
      .pipe(
        catchError((error) => {
          this.loading = false;
          this.actionInProgress = false;
          return this.loadData(1);
        })
      )
      .pipe(
        tap(() => {
          this.subscribeToDataChange();
        })
      )
      .pipe(
        tap(() => {
          this.loading = false;
          this.actionInProgress = false;
        })
      );
  }

  private initializeFilters() {
    if (this.filters) {
      return;
    }

    const params = this.route.snapshot.queryParams;

    this.filters = {};
    if (!this.currentTab.tableView.filters) {
      return;
    }

    this.currentTab.tableView.filters.forEach((filter) => (filter.value = ''));
    this.currentTab.tableView.filters.forEach((f) => {
      this.filters[f.name] = f;
      if (params[f.name]) {
        if (f.type === 'multi_option' && !Array.isArray(params[f.name])) {
          this.filters[f.name].value = [params[f.name]] as any;
        } else {
          this.filters[f.name].value = params[f.name];
        }
      }
    });

    this.filterOn = Object.keys(this.filters).filter((key) => this.filters[key].value).length > 0;
  }

  private getData(pageRequest: PageRequestDTO): Observable<PageResponseDTO<any>> {
    if (this.isThereRequiredFilter()) {
      this.loading = false;
      // this.filtersNotFilled = true;
    }

    return this.dataSource.doGetData(
      this.menuConfig,
      this.currentTab,
      pageRequest,
      this.filters,
      this.id,
      this.context || {},
      this.route.snapshot.queryParams
    );
  }

  public onColumnsFilter(filteredColumns: string[]) {
    const params: Params = this.route.snapshot.queryParams;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...params,
        filteredColumns: filteredColumns.join(','),
      },
    });
  }

  private constructTableData(data: PageResponseDTO<any>) {
    this.filteredFieldNames = (this.route.snapshot.queryParams.filteredColumns || undefined)?.split(',') || [];

    this.allFields = this.constructFieldsArray(data);

    /*
                if (this.menuConfig && this.menuConfig.elasticConfig && this.menuConfig.elasticConfig.withTimestamp) {
                    this.allFields.unshift(this.timestamp);
                } */

    this.filterableFields = this.allFields.filter((f) => f.displayInTable).filter((f) => !f.columnNotFilterable);

    this.displayedFieldNames = this.allFields
      .filter((f) => f.displayInTable)
      .filter(
        (f) => this.filteredFieldNames.length === 0 || f.columnNotFilterable || this.filteredFieldNames.includes(f.name)
      )
      .map((f) => f.name);
    this.displayedFieldNames.push('actions');
    if (this.isExpandable()) {
      this.displayedFieldNames.unshift('expander');
    }

    data.content.forEach((row) => (row.conditional = MaintainTableComponent.getConditionalByTab(row, this.currentTab)));

    const matTableDataSource = new MatTableDataSource(data.content);

    if (this.dataSource.isInMemoryFilter(this.menuConfig)) {
      matTableDataSource.filterPredicate = (data, filter) => {
        let filterStr = '';
        const actualData = (data as any).data;
        Object.keys(actualData).forEach((k) => {
          const tickerData = actualData[k];
          if (tickerData) {
            filterStr += String(tickerData);
            Object.keys(tickerData).forEach((k2) => (filterStr += String(tickerData[k2])));
          }
        });
        return filter.split(',').every((f) => filterStr.toLowerCase().trim().includes(f.toLowerCase().trim()));
      };
    }

    this.initializeSorting();
    this.tableData = matTableDataSource;
    this.data = data;
    this.initializePaging();
    this.changeDetector.detectChanges();

    if (this.inMemoryFilterValue) {
      this.applyFilter(this.inMemoryFilterValue);
    }
  }

  private constructFieldsArray(data: PageResponseDTO<any>) {
    const staticColumns = this.dataSource
      .getRows(this.menuConfig)
      .flatMap((r) => r.columns)
      .flatMap((c) => c.fields)
      .map((f) => {
        f.sticky = f?.tableCss?.includes('sticky');
        return f;
      });
    const sourceElementFOrDynamicColumns = data.content.length > 0 ? data.content[0] : {};

    Object.keys(sourceElementFOrDynamicColumns)
      .filter((key) => key.endsWith('DynamicColumns'))
      .map((key) => sourceElementFOrDynamicColumns[key])
      .flatMap((l) => l)
      .forEach((dynamicColumn) => {
        dynamicColumn.fieldConfig.sticky = dynamicColumn.fieldConfig?.tableCss?.includes('sticky');
        staticColumns.push(dynamicColumn.fieldConfig);
      });

    data.content.forEach((row) => {
      Object.keys(row)
        .filter((key) => key.endsWith('DynamicColumns'))
        .map((key) => row[key])
        .flatMap((l) => l)
        .forEach((dynamicColumn) => {
          row[dynamicColumn.fieldConfig.name] = dynamicColumn.restriction;
        });
    });

    return staticColumns;
  }

  refreshContent() {
    if (isDefined(this.dataSource)) {
      this.doLoadDataAndFInishProcesisng().pipe(take(1)).subscribe();
    }
  }

  getEntityCode(): string {
    return (
      this.route.snapshot.paramMap.get('entityCode') ??
      this.route.snapshot.data['entityCodeOverride'] ??
      this.entityCode
    );
  }

  expandRow(row) {
    row.expanded = !row.expanded;
  }

  onRowClick(row, event) {
    if (event.target.tagName === 'MAT-ICON') {
      return;
    }
    if (!this.hasDetails()) {
      return;
    }

    let url = `/${this.router.url.split('?')[0]}/${this.maintainService.createAppIdForEntity(this.menuConfig, row)}`;
    if (this.menuConfig.menuType === MenuType.ENTITY_GROUP) {
      url += `/${this.menuConfig.entityGroup.menuItems[0].code}`;
    }

    this.router.navigate([url], {
      queryParams: {
        cohortId: this.route.snapshot.queryParams.cohortId,
        _mode: this.mode,
      },
    });
  }

  private hasDetails() {
    return (
      this.menuConfig.entityGroup &&
      this.menuConfig.entityGroup.menuItems &&
      this.menuConfig.entityGroup.menuItems.length > 0
    );
  }

  addEntity() {
    const url =
      this.menuConfig.menuType === MenuType.ENTITY_GROUP
        ? `${this.router.url.split('?')[0]}/group/add`
        : `${this.router.url.split('?')[0]}/add`;
    this.router.navigate([url]);
  }

  getTableTitle() {
    if (isDefined(this.dataSource)) {
      return this.dataSource.getTitle(this.menuConfig);
    }
    return 'missing';
  }

  hasHelp(): boolean {
    if (this.menuConfig !== undefined && this.menuConfig.help !== undefined && this.menuConfig.help !== '') {
      return true;
    }
    return false;
  }

  getHelp() {
    if (this.menuConfig !== undefined && this.menuConfig.help !== undefined && this.menuConfig.help !== '') {
      if (window.location.hostname === 'localhost') {
        // assuming split - heh its me!
        const win = window.open(`http://127.0.0.1:8300/admin/doc/${this.menuConfig.help}`, '_blank');
        win.focus();
        return;
      }
      const win = window.open(`/admin/doc/${this.menuConfig.help}`, '_blank');
      win.focus();
    }
  }

  private handleMultiTabView() {
    const tabs = this.dataSource.getTabs(this.menuConfig);
    if (tabs !== undefined && tabs !== null) {
      this.tabs = tabs;

      const page = this.route.snapshot.queryParamMap.get('tab');

      if (!page && this.tabs.length > 1) {
        const { queryParams } = this.route.snapshot;

        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            ...queryParams,
            tab: this.tabs[0].tableView.code,
          },
        });
      }
      this.currentTab = this.tabs.find((t) => t.tableView.code === page);
      if (!this.currentTab) {
        this.currentTab = this.tabs[0];
      }
      this.initializeFilters();
    }
  }

  toggleFilter() {
    this.filterOn = !this.filterOn;
  }

  handleFilter(filterValues: { [key: string]: string }) {
    const params: Params = this.route.snapshot.queryParams;
    const newParams = {};

    Object.keys(this.filters).forEach((k) => {
      const value = filterValues[k];
      this.filters[k].value = value;
      if (value && String(value).length > 0) {
        newParams[this.filters[k].name] = typeof value === 'object' ? JSON.stringify(value) : value;
      } else {
        newParams[this.filters[k].name] = null;
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...params,
        ...newParams,
        offset: 0,
      },
    });
  }

  isThereRequiredFilter(): boolean {
    let returnValue = false;
    if (this.filters !== undefined && this.filters !== null) {
      Object.keys(this.filters).forEach((k) => {
        const filterConfigDTO = this.filters[k];
        const isRequired =
          filterConfigDTO.validators && filterConfigDTO.validators.find((v) => v.type === UiValidatorType.NOT_NULL);
        if (!!isRequired && (!filterConfigDTO.value || filterConfigDTO.value === '')) {
          returnValue = true;
        }
      });
    }
    return returnValue;
  }

  getExpandedColumnName(): string[] {
    return this.isExpandable() ? ['expandedDetail'] : [];
  }

  isExpandable(): boolean {
    if (!this.tabs) {
      return false;
    }
    const { conditionals } = this.tabs[0].tableView;
    if (!conditionals || conditionals.length < 1) {
      return false;
    }

    return !!conditionals.find((c) => c.expandable);
  }

  getConditional(element: any) {
    const tab = this.getCurrentTab();
    return MaintainTableComponent.getConditionalByTab(element, tab);
  }

  public getCurrentTab() {
    let tab: MaintainTabDTO;
    const tabCode = this.route.snapshot.queryParamMap.get('tab');
    if (!tabCode) {
      tab = this.tabs[0];
    } else {
      tab = this.tabs.find((t) => t.tableView.code === tabCode);
    }
    return tab;
  }

  ngOnDestroy(): void {
    this.mainSubscription.unsubscribe();
    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }
  }

  shouldHideFilters() {
    return !this.tabs[0].tableView.filters || !(this.tabs[0].tableView.filters.length > 0) || !this.filterOn;
  }

  addAction(action: MaintainTableRowActionsComponent) {
    this.actions.push(action);
  }

  private subscribeToDataChange() {
    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }
    const pageRequest = this.getCurrentPage();
    this.dataSourceSubscription = this.dataSource
      .dataChanged(
        this.menuConfig,
        this.currentTab,
        pageRequest,
        this.filters ? this.filters : {},
        this.id,
        this.context,
        {}
      )
      .subscribe((data) => {
        const pageBeforeFilter = this._matPaginator.pageIndex;
        this.data = data;
        this.constructTableData(data);
        this.matTable.renderRows();
        this.changeDetector.detectChanges();
        this.actions.forEach((a) => a.dataUpdated(data.content.find((d) => d.id === a.id)));

        if (this.inMemoryFilterValue && this.inMemoryFilterValue.length > 0) {
          this.tableData.filter = this.inMemoryFilterValue.trim().toLowerCase();
        }
        this.maintainService.rowActionsChangeSubscriber.next(true);
        if (pageBeforeFilter < this._matPaginator.getNumberOfPages()) {
          this._matPaginator.pageIndex = pageBeforeFilter;
        }
      });
  }

  private getCurrentPage(): PageRequestDTO {
    const params: Params = this.route.snapshot.queryParams;

    const { offset } = params;
    const { perPage } = params;

    const pageRequest: PageRequestDTO = {
      offset,
      perPage,
    };

    if (params.order) {
      pageRequest.order = params.order;
    }

    // if (params['pageStates']) {
    pageRequest.pageStates = params.pageStates;
    // }

    return pageRequest;
  }

  getClasses(row: any) {
    if (!(row.locked && row.locked.email && row.locked.email.length > 0)) {
      return [];
    }

    const { email } = row.locked;
    if (row.locked.markedToRemove) {
      return ['marked-as-removed'];
    }
    if (email === this.signInService.getCurrentUser().email) {
      return ['locked-by-you'];
    }
    return ['locked-by-other'];
  }

  applyFilter(filterValue: string) {
    this.inMemoryFilterValue = filterValue.trim().toLowerCase();
    this.tableData.filter = filterValue.trim().toLowerCase();

    if (this.tableData.paginator) {
      this.tableData.paginator.firstPage();
    }

    this.subscribeToDataChange();
  }

  getClassesForTabHeader(field: FieldConfigDTO) {
    switch (field.align) {
      case 'right':
        return ['header-align-right'];
      case 'center':
        return ['header-align-center'];
      case 'default':
      case 'left':
      default:
        return ['header-align-left'];
    }
  }

  onSortChange(event) {
    let orderString;
    if (event.direction) {
      const active = this.allFields.find((f) => f.name == event.active).sortableField;
      orderString = `${active},${event.direction.toUpperCase()}`;
    } else {
      orderString = null;
    }

    if (this.loading) {
      return;
    }

    const innerParams: Params = this.route.snapshot.queryParams;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        ...innerParams,
        order: orderString,
        offset: this.dataSource.getPageMode(this.menuConfig) === PAGINATION_MODE.SERVER_SIDE ? 0 : undefined,
      },
    });
  }

  pageChanged(event: PageEvent) {
    const pageMode = this.dataSource.getPageMode(this.menuConfig);
    if (pageMode === PAGINATION_MODE.SERVER_SIDE) {
      const theSameSize = this.getCurrentPage().perPage == event.pageSize;
      const oldParams: Params = this.route.snapshot.queryParams;

      const queryParams = {
        ...oldParams,
        offset: theSameSize ? event.pageIndex : 0,
        perPage: event.pageSize,
      };

      // if (isDefined(this.data.pageStates)) { // arrayNotEmpty
      queryParams['pageStates'] = this.data.pageStates;
      // }

      const last = !this._matPaginator.hasNextPage();
      const first = !this._matPaginator.hasPreviousPage();
      queryParams['last'] = last;
      queryParams['before'] = null;
      queryParams['after'] = null;

      if (!last && !first && event.pageIndex > event.previousPageIndex) {
        const after = this.extractAttachSeparatorElement(this.data.content[this.data.content.length - 1], oldParams);
        queryParams['after'] = after;
      } else if (!last && !first && event.pageIndex < event.previousPageIndex) {
        const before = this.extractAttachSeparatorElement(this.data.content[0], oldParams);
        queryParams['before'] = before;
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
      });
    } else if (pageMode === PAGINATION_MODE.IN_MEMORY) {
    }
  }

  showFirstLastButtons(): boolean {
    try {
      return this.menuConfig.entityGroup.dataSources[0].type !== MenuType.CASSANDRA;
    } catch (err) {
      return true;
    }
  }

  shouldDisplayPaginator() {
    return (
      this.dataSource &&
      this.dataSource.getPageMode(this.menuConfig) !== PAGINATION_MODE.NONE &&
      !this.loading &&
      this.data &&
      this.data.content &&
      this.currentTab.tableView.paged
    );
  }

  canAdd(): boolean {
    return (
      (this.currentTab && this.currentTab.tableView.add && this.currentTab.tableView.add.allowAdd) ||
      (this.menuConfig.entityGroup && !!this.menuConfig.entityGroup.createConfig)
    );
  }

  actionStartedForRow(element) {
    this.actionInProgress = true;
  }

  actionFinishedForRow(responseContent: any, element) {
    this.refreshContent();
    this.actionInProgress = false;
    const index = this.tableData.data.findIndex((el) => el.id === element.id);
  }

  private extractAttachSeparatorElement(any: any, params: Params) {
    const orderParam = params.order;
    if (!orderParam) {
      return null;
    }

    const orderFieldName = EncodingUtils.kebabCaseToCamel(orderParam.split(',')[0]);
    const orderField = this.getAttachFieldValue(orderFieldName, any);
    const tieBreakerField = this.getAttachFieldValue(
      EncodingUtils.kebabCaseToCamel(this.currentTab.tableView.sortTieBreaker),
      any
    );

    return orderField && tieBreakerField ? `${orderField},${tieBreakerField}` : null;
  }

  private getAttachFieldValue(orderFieldName, element) {
    const orderFieldConfig = this.allFields.find((f) => f.name === orderFieldName);
    const isDate = orderFieldConfig && [DataType.date, DataType.date_time].includes(DataType[orderFieldConfig.type]);
    const orderField = element[orderFieldName];
    return isDate ? Date.parse(orderField) : orderField;
  }

  transform(style: string): SafeStyle {
    if (style === undefined) {
      return '';
    }
    return this.domSanitizer.bypassSecurityTrustStyle(style);
  }
}
